import { Eye, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { endView, getView } from "@/api/session";
import { VIEW_ONLY_EVENT } from "@/api/viewStore";
import { useSession } from "@/app/session";
import { Button } from "@/components/ui";

import { viewRoleLabel } from "./roleCard";

function minutesLeft(expiresAt: number, now: number): number {
  return Math.max(0, Math.ceil((expiresAt - now) / 60_000));
}

/**
 * A view tab (Admin → Users → "View as this user", V044): a loud bar above the
 * header so the admin never mistakes it for their own session, with the time
 * left and "Close view". Also turns the API's 403 view_only into a friendly
 * note, and ends the view when its hour is up.
 */
export function ViewBanner() {
  const { user } = useSession();
  const view = getView();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!view) return;
    const t = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(t);
  }, [view]);

  useEffect(() => {
    if (view && now >= view.expiresAt) void endView();
  }, [view, now]);

  useEffect(() => {
    const onRefused = () =>
      toast.info("View only", {
        id: "view-only",
        position: "bottom-center", // clear of the banner's Close view button
        description: "You're viewing this account as an admin, so changes are turned off.",
      });
    window.addEventListener(VIEW_ONLY_EVENT, onRefused);
    return () => window.removeEventListener(VIEW_ONLY_EVENT, onRefused);
  }, []);

  if (!view || !user) return null;
  const name = user.display_name || user.email;
  const mins = minutesLeft(view.expiresAt, now);
  return (
    <div
      role="status"
      aria-label="Viewing as another user"
      data-testid="view-banner"
      className="bg-gradient-to-r from-[#f59e0b] via-[#f8a51c] to-[#f97316] px-3 py-1.5 text-xs text-[#231400] sm:px-5"
    >
      <div className="mx-auto flex max-w-[1600px] min-w-0 items-center gap-2">
        <Eye aria-hidden className="size-4 shrink-0" />
        <p className="min-w-0 flex-1 leading-snug">
          <span className="font-semibold break-words">
            Viewing {name}'s dashboard ({view.label ?? viewRoleLabel(user)})
          </span>
          <span className="hidden sm:inline"> as admin</span>
          <span> · View-only · {mins} min left</span>
        </p>
        <Button
          size="sm"
          className="h-7 shrink-0 bg-[#231400] px-2.5 text-white hover:bg-[#231400]/85"
          onClick={() => void endView()}
        >
          <X aria-hidden />
          Close view
        </Button>
      </div>
    </div>
  );
}
